import type { JSX } from 'react';
import { useState } from 'react';
import { Alert, Badge, Center, Group, Loader, Stack, Text, TextInput } from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, dinApi, type DinGroup } from '../../api';
import { FileDrop } from '../../ui/FileDrop';
import { LibraryTable, type LibraryColumn } from '../../ui/LibraryTable';

const COLUMNS: LibraryColumn<DinGroup>[] = [
  { key: 'code', title: 'KG', defaultWidth: 70, render: (g) => <Text size="sm" ff="monospace" fw={g.level === 1 ? 700 : undefined} pl={(g.level - 1) * 10}>{g.code}</Text> },
  { key: 'name', title: 'Bezeichnung', defaultWidth: 260, minWidth: 80, render: (g) => <Text size="sm" fw={g.level < 3 ? 600 : undefined}>{g.name}</Text> },
  { key: 'note', title: 'Anmerkung', defaultWidth: 420, minWidth: 80, render: (g) => <Text size="xs" style={{ whiteSpace: 'pre-line' }}>{g.note}</Text> },
  { key: 'page', title: 'Seite', defaultWidth: 60, align: 'right', render: (g) => <Text size="xs" c="dimmed">{g.page}</Text> },
];

/**
 * Verwaltung › Bibliotheken › DIN 276 (AP-85): the office's copy of the cost
 * group outline with the standard's notes, to look things up, and the source
 * of the full outline in every cost plan. Read in by the administration from
 * a CSV made from the licensed standard (R-24).
 */
export function DinCatalog({ canManage }: { canManage: boolean }): JSX.Element {
  const queryClient = useQueryClient();
  const list = useQuery({ queryKey: ['din'], queryFn: dinApi.list });
  const [query, setQuery] = useState('');
  const [edition, setEdition] = useState('DIN 276:2018-12');
  const read = useMutation({
    mutationFn: async (file: File) => dinApi.import(await file.text(), edition.trim()),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['din'] }),
  });
  const words = query.toLowerCase().split(/\s+/).filter(Boolean);
  const all = list.data ?? [];
  const shown = all.filter((g) => words.every((w) => `${g.code} ${g.name} ${g.note ?? ''}`.toLowerCase().includes(w)));

  return (
    <Stack gap="sm">
      <Text size="xs" c="dimmed">
        Kostengruppen nach DIN 276 mit den Anmerkungen der Norm, aus dem lizenzierten Exemplar des Büros. Jeder Kostenplan kann daraus die vollständige Gliederung übernehmen; die Anmerkung erscheint dort an der Kostengruppe (ⓘ).
      </Text>
      {list.isPending && <Center h={80}><Loader size="sm" /></Center>}
      {list.isError && <Alert color="red">Die Liste konnte nicht geladen werden.</Alert>}
      {list.data && all.length === 0 && <Text size="sm" c="dimmed">Noch nicht eingelesen.</Text>}
      {all.length > 0 && (
        <Group gap="sm">
          <TextInput size="xs" placeholder="Suchen: Nummer, Bezeichnung, Anmerkung …" value={query} onChange={(e) => setQuery(e.currentTarget.value)} style={{ flex: 1 }} />
          <Text size="xs" c="dimmed">{shown.length} von {all.length} · {all[0]!.edition}</Text>
        </Group>
      )}
      {shown.length > 0 && (
        // Read in as a whole only: an entry is looked at, not changed (AP-128).
        <LibraryTable<DinGroup>
          tableId="din276"
          label="Kostengruppen nach DIN 276"
          columns={COLUMNS}
          rows={shown}
          rowKey={(g) => g.code}
          rowLabel={(g) => `${g.code} ${g.name}`}
          rowStyle={(g) => (g.level === 1 ? { background: 'var(--mantine-color-default-hover)' } : undefined)}
          canManage={canManage}
          fields={(g) => [
            { key: 'code', label: 'Kostengruppe', value: g.code, mono: true },
            { key: 'name', label: 'Bezeichnung', value: g.name },
            { key: 'level', label: 'Ebene', value: String(g.level) },
            { key: 'page', label: 'Seite', value: g.page === null ? null : String(g.page) },
            { key: 'edition', label: 'Ausgabe', value: g.edition },
            { key: 'note', label: 'Anmerkung', value: g.note, long: true },
          ]}
          resetKey={query}
        />
      )}
      {canManage && (
        <Stack gap="xs">
          <Text size="sm" fw={500}>Einlesen <Badge size="xs" variant="light" color="gray">Administration</Badge></Text>
          <Text size="xs">CSV mit den Spalten <Text span ff="monospace">kg;bezeichnung;anmerkung;seite</Text>. Ersetzt die ganze Liste; Kostenpläne behalten ihre Gruppen und Bezeichnungen.</Text>
          <TextInput size="xs" label="Ausgabe" value={edition} onChange={(e) => setEdition(e.currentTarget.value)} w={220} />
          <FileDrop accept=".csv" label="CSV-Datei hierher ziehen oder auswählen" onFiles={(files) => files[0] && read.mutate(files[0])} disabled={read.isPending || edition.trim().length < 3} />
          {read.isSuccess && <Alert color="green" py={6}>{read.data.groups} Kostengruppen eingelesen.</Alert>}
          {read.isError && <Alert color="red" py={6}>{read.error instanceof ApiError ? read.error.message : 'Einlesen fehlgeschlagen.'}</Alert>}
        </Stack>
      )}
    </Stack>
  );
}
